import { useRef, useEffect, useCallback, useState } from 'react'

const SEGMENTS = [
  { label: 'TRY AGAIN',  bg: '#F0F0F0', bgAlt: '#E4E4E4', accent: '#C0C0C0', text: '#999999', tier: 0 },
  { label: 'FREE SPIN',  bg: '#BBFAD4', bgAlt: '#86EFAC', accent: '#059669', text: '#065F46', tier: 1 },
  { label: 'TRY AGAIN',  bg: '#EAEAEA', bgAlt: '#DEDEDE', accent: '#C0C0C0', text: '#999999', tier: 0 },
  { label: '5 USDT',     bg: '#C7DDFF', bgAlt: '#A5C8FF', accent: '#2563EB', text: '#1E3A8A', tier: 2 },
  { label: 'TRY AGAIN',  bg: '#F0F0F0', bgAlt: '#E4E4E4', accent: '#C0C0C0', text: '#999999', tier: 0 },
  { label: 'FREE SPIN',  bg: '#A7F3D0', bgAlt: '#6EE7B7', accent: '#059669', text: '#065F46', tier: 1 },
  { label: '100 USDT',   bg: '#DFC8FF', bgAlt: '#C9A5FF', accent: '#7C3AED', text: '#4C1D95', tier: 3 },
  { label: '5 USDT',     bg: '#A5C8FF', bgAlt: '#7DB2FF', accent: '#2563EB', text: '#1E3A8A', tier: 2 },
  { label: 'iPHONE 17',  bg: '#FFE066', bgAlt: '#FFD426', accent: '#B45309', text: '#713F12', tier: 4 },
  { label: '100 USDT',   bg: '#C9A5FF', bgAlt: '#B088FF', accent: '#7C3AED', text: '#4C1D95', tier: 3 },
]

const NUM = SEGMENTS.length
const ARC = (Math.PI * 2) / NUM

interface Particle {
  x: number; y: number; vx: number; vy: number
  life: number; maxLife: number; size: number; color: string
}

interface PrizeWheelProps {
  spinning: boolean
  targetIndex?: number
  onSpin: () => void
  onSpinComplete?: () => void
  disabled: boolean
}

export default function PrizeWheel({ spinning, targetIndex, onSpin, onSpinComplete, disabled }: PrizeWheelProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const rotRef = useRef(0)
  const frameRef = useRef(0)
  const busyRef = useRef(false)
  const timeRef = useRef(0)
  const speedRef = useRef(0)
  const particlesRef = useRef<Particle[]>([])
  const [hovered, setHovered] = useState(false)
  const onSpinCompleteRef = useRef(onSpinComplete)
  onSpinCompleteRef.current = onSpinComplete

  const draw = useCallback((rot: number, time: number = 0, speed: number = 0) => {
    const cvs = canvasRef.current
    if (!cvs) return
    const ctx = cvs.getContext('2d')
    if (!ctx) return
    const dpr = window.devicePixelRatio || 1
    const S = cvs.width / dpr
    const cx = S / 2, cy = S / 2
    const R = S * 0.42
    const rimW = S * 0.03
    const hubR = R * 0.13

    ctx.save()
    ctx.scale(dpr, dpr)
    ctx.clearRect(0, 0, S, S)

    // === STRONG DROP SHADOW for 3D depth ===
    ctx.save()
    ctx.shadowColor = 'rgba(0,0,0,0.28)'
    ctx.shadowBlur = S * 0.1
    ctx.shadowOffsetY = S * 0.025
    ctx.beginPath(); ctx.arc(cx, cy, R + rimW, 0, Math.PI * 2)
    ctx.fillStyle = '#E0E0E0'
    ctx.fill()
    ctx.restore()

    // Speed lines during fast spin
    if (speed > 0.05) {
      const lineCount = Math.min(Math.floor(speed * 30), 15)
      for (let i = 0; i < lineCount; i++) {
        const a = Math.random() * Math.PI * 2
        const r1 = R * 1.05
        const r2 = R * (1.12 + speed * 0.4)
        const alpha = speed * 0.15 * Math.random()
        ctx.save()
        ctx.translate(cx, cy)
        ctx.beginPath()
        ctx.moveTo(Math.cos(a) * r1, Math.sin(a) * r1)
        ctx.lineTo(Math.cos(a) * r2, Math.sin(a) * r2)
        ctx.strokeStyle = `rgba(200,200,200,${alpha})`
        ctx.lineWidth = 1
        ctx.stroke()
        ctx.restore()
      }
    }

    // === SEGMENTS ===
    ctx.save()
    ctx.translate(cx, cy)
    ctx.rotate(rot)

    for (let i = 0; i < NUM; i++) {
      const s = SEGMENTS[i]
      const a0 = i * ARC - Math.PI / 2
      const a1 = a0 + ARC
      const mid = a0 + ARC / 2

      // Segment fill — light gradient
      const grd = ctx.createRadialGradient(0, 0, R * 0.08, 0, 0, R)
      grd.addColorStop(0, s.bg)
      grd.addColorStop(0.65, s.bg)
      grd.addColorStop(1, s.bgAlt)
      ctx.beginPath(); ctx.moveTo(0, 0); ctx.arc(0, 0, R, a0, a1); ctx.closePath()
      ctx.fillStyle = grd; ctx.fill()

      // Colored accent arc near rim for prize segments
      if (s.tier > 0) {
        ctx.beginPath(); ctx.arc(0, 0, R * 0.96, a0 + 0.02, a1 - 0.02)
        ctx.strokeStyle = s.accent + '90'
        ctx.lineWidth = 5; ctx.stroke()
      }

      // Divider lines
      ctx.beginPath(); ctx.moveTo(0, 0)
      ctx.lineTo(Math.cos(a0) * R, Math.sin(a0) * R)
      ctx.strokeStyle = 'rgba(0,0,0,0.10)'; ctx.lineWidth = 1.5; ctx.stroke()

      // === TEXT — single line, large and readable ===
      ctx.save()
      ctx.rotate(mid)
      const flip = Math.cos(mid) < 0
      if (flip) ctx.rotate(Math.PI)
      const tx = flip ? -R * 0.60 : R * 0.60

      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'

      // Much bigger font, extra bold for high-tier prizes
      const baseFs = S * 0.042
      let fs = baseFs
      if (s.tier >= 4) fs = baseFs * 1.1
      else if (s.tier >= 3) fs = baseFs * 1.0
      else if (s.label.length > 8) fs = baseFs * 0.85
      else if (s.tier === 0) fs = baseFs * 0.82
      const weight = s.tier >= 3 ? '900' : s.tier >= 1 ? '800' : '700'
      ctx.font = `${weight} ${fs}px "Inter", system-ui, sans-serif`
      ctx.fillStyle = s.text

      // Text shadow for readability
      if (s.tier >= 2) {
        ctx.shadowColor = 'rgba(255,255,255,0.8)'
        ctx.shadowBlur = 4
        ctx.shadowOffsetX = 0
        ctx.shadowOffsetY = 0
      }

      ctx.fillText(s.label, tx, 0)
      ctx.shadowColor = 'rgba(0,0,0,0)'
      ctx.shadowBlur = 0
      ctx.restore()
    }

    // 3D depth overlay — strong top highlight, bottom shadow
    const depthGrd = ctx.createLinearGradient(0, -R, 0, R)
    depthGrd.addColorStop(0, 'rgba(255,255,255,0.18)')
    depthGrd.addColorStop(0.35, 'rgba(255,255,255,0.03)')
    depthGrd.addColorStop(0.55, 'rgba(0,0,0,0.0)')
    depthGrd.addColorStop(1, 'rgba(0,0,0,0.12)')
    ctx.beginPath(); ctx.arc(0, 0, R, 0, Math.PI * 2)
    ctx.fillStyle = depthGrd; ctx.fill()

    // Inner vignette for 3D curvature — stronger
    const vignette = ctx.createRadialGradient(0, 0, R * 0.4, 0, 0, R)
    vignette.addColorStop(0, 'rgba(0,0,0,0)')
    vignette.addColorStop(0.8, 'rgba(0,0,0,0)')
    vignette.addColorStop(1, 'rgba(0,0,0,0.1)')
    ctx.beginPath(); ctx.arc(0, 0, R, 0, Math.PI * 2)
    ctx.fillStyle = vignette; ctx.fill()

    ctx.restore()

    // === RIM — beveled with highlight ===
    const rimR = R + rimW / 2

    // Outer rim — metallic gradient with orange accents
    const rimGrad = ctx.createLinearGradient(cx, cy - R, cx, cy + R)
    rimGrad.addColorStop(0, '#D0D0D0')
    rimGrad.addColorStop(0.1, '#F0F0F0')
    rimGrad.addColorStop(0.2, '#FFFFFF')
    rimGrad.addColorStop(0.35, '#E0C8A0')
    rimGrad.addColorStop(0.5, '#D4D4D4')
    rimGrad.addColorStop(0.65, '#E0C8A0')
    rimGrad.addColorStop(0.8, '#B8B8B8')
    rimGrad.addColorStop(0.9, '#A0A0A0')
    rimGrad.addColorStop(1, '#909090')

    ctx.beginPath(); ctx.arc(cx, cy, rimR, 0, Math.PI * 2)
    ctx.lineWidth = rimW; ctx.strokeStyle = rimGrad; ctx.stroke()

    // Orange accent ring inside rim
    ctx.beginPath(); ctx.arc(cx, cy, R + 1, 0, Math.PI * 2)
    ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(255,107,0,0.3)'; ctx.stroke()

    // Outer edge highlight
    ctx.beginPath(); ctx.arc(cx, cy, R + rimW, 0, Math.PI * 2)
    ctx.lineWidth = 1; ctx.strokeStyle = 'rgba(255,255,255,0.6)'; ctx.stroke()

    // Outer edge shadow
    ctx.beginPath(); ctx.arc(cx, cy, R + rimW + 1, 0, Math.PI * 2)
    ctx.lineWidth = 1; ctx.strokeStyle = 'rgba(0,0,0,0.1)'; ctx.stroke()

    // Inner edge
    ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2)
    ctx.lineWidth = 1; ctx.strokeStyle = 'rgba(0,0,0,0.08)'; ctx.stroke()

    // Tick marks around rim
    const nTicks = 48
    for (let i = 0; i < nTicks; i++) {
      const a = (i / nTicks) * Math.PI * 2
      const r1 = R + rimW + 1
      const r2 = R + rimW + S * 0.008
      ctx.beginPath()
      ctx.moveTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1)
      ctx.lineTo(cx + Math.cos(a) * r2, cy + Math.sin(a) * r2)
      ctx.strokeStyle = i % 12 === 0 ? 'rgba(255,107,0,0.4)' : 'rgba(0,0,0,0.06)'
      ctx.lineWidth = i % 12 === 0 ? 1.5 : 0.6
      ctx.stroke()
    }

    // === CENTER HUB — 3D dome ===
    // Hub shadow
    ctx.save()
    ctx.shadowColor = 'rgba(0,0,0,0.2)'
    ctx.shadowBlur = 12
    ctx.shadowOffsetY = 3
    ctx.beginPath(); ctx.arc(cx, cy, hubR, 0, Math.PI * 2)
    ctx.fillStyle = '#E8E8E8'
    ctx.fill()
    ctx.restore()

    // Hub gradient — dome highlight
    const hg = ctx.createRadialGradient(cx - hubR * 0.2, cy - hubR * 0.25, hubR * 0.1, cx, cy, hubR)
    hg.addColorStop(0, '#FFFFFF')
    hg.addColorStop(0.4, '#F5F5F5')
    hg.addColorStop(0.8, '#E0E0E0')
    hg.addColorStop(1, '#CCCCCC')
    ctx.beginPath(); ctx.arc(cx, cy, hubR, 0, Math.PI * 2)
    ctx.fillStyle = hg; ctx.fill()

    // Hub border
    ctx.beginPath(); ctx.arc(cx, cy, hubR, 0, Math.PI * 2)
    ctx.lineWidth = 1.5
    ctx.strokeStyle = 'rgba(0,0,0,0.15)'
    ctx.stroke()

    // Hub center dot — subtle pulse
    const hubPulse = 0.15 + Math.sin(time * 2) * 0.05
    ctx.beginPath(); ctx.arc(cx, cy, hubR * 0.25, 0, Math.PI * 2)
    ctx.fillStyle = `rgba(255,107,0,${hubPulse})`
    ctx.fill()

    // === POINTER (top) ===
    const pw = S * 0.03, ph = S * 0.055
    const ptop = cy - R - rimW - S * 0.008

    ctx.save()
    ctx.shadowColor = 'rgba(0,0,0,0.25)'
    ctx.shadowBlur = 10
    ctx.shadowOffsetY = 3

    const pg = ctx.createLinearGradient(cx, ptop, cx, ptop + ph)
    pg.addColorStop(0, '#FF8533')
    pg.addColorStop(0.5, '#FF6B00')
    pg.addColorStop(1, '#CC5500')
    ctx.beginPath()
    ctx.moveTo(cx, ptop + ph)
    ctx.lineTo(cx - pw, ptop)
    ctx.lineTo(cx + pw, ptop)
    ctx.closePath()
    ctx.fillStyle = pg; ctx.fill()
    ctx.strokeStyle = 'rgba(0,0,0,0.12)'; ctx.lineWidth = 1; ctx.stroke()
    ctx.restore()

    // Pointer highlight
    ctx.save()
    ctx.beginPath()
    ctx.moveTo(cx, ptop + ph - 2)
    ctx.lineTo(cx - pw * 0.5, ptop + 4)
    ctx.lineTo(cx + pw * 0.1, ptop + 4)
    ctx.closePath()
    ctx.fillStyle = 'rgba(255,255,255,0.25)'
    ctx.fill()
    ctx.restore()

    // === PARTICLES ===
    const pts = particlesRef.current
    for (let i = pts.length - 1; i >= 0; i--) {
      const p = pts[i]
      p.x += p.vx; p.y += p.vy
      p.life -= 0.016
      if (p.life <= 0) { pts.splice(i, 1); continue }
      const alpha = (p.life / p.maxLife)
      ctx.save()
      ctx.shadowColor = p.color
      ctx.shadowBlur = 6
      ctx.beginPath(); ctx.arc(p.x, p.y, p.size * alpha, 0, Math.PI * 2)
      ctx.fillStyle = p.color.replace(')', `,${alpha})`)
        .replace('rgb(', 'rgba(')
        .replace('rgba(', 'rgba(')
      const [r, g, b] = p.color.match(/\d+/g)?.map(Number) || [0, 232, 224]
      ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`
      ctx.fill()
      ctx.restore()
    }

    // Emit particles during spin
    if (speed > 0.02) {
      const emitCount = Math.min(Math.floor(speed * 8), 4)
      for (let i = 0; i < emitCount; i++) {
        const a = Math.random() * Math.PI * 2
        const r = R + rimW + S * 0.02
        pts.push({
          x: cx + Math.cos(a) * r,
          y: cy + Math.sin(a) * r,
          vx: Math.cos(a) * (1 + Math.random() * 2),
          vy: Math.sin(a) * (1 + Math.random() * 2),
          life: 0.5 + Math.random() * 0.5,
          maxLife: 1,
          size: 1.5 + Math.random() * 2,
          color: Math.random() > 0.5 ? 'rgb(255,170,100)' : 'rgb(210,210,210)',
        })
      }
    }

    ctx.restore()
  }, [])

  // Initial draw
  useEffect(() => {
    const cvs = canvasRef.current
    const container = containerRef.current
    if (!cvs || !container) return
    const dpr = window.devicePixelRatio || 1
    const w = container.getBoundingClientRect().width
    const vh = window.innerHeight
    const maxSz = Math.min(vh * 0.48, 540)
    const sz = Math.max(Math.min(w, maxSz), 260)
    cvs.width = sz * dpr; cvs.height = sz * dpr
    cvs.style.width = `${sz}px`; cvs.style.height = `${sz}px`
    draw(rotRef.current, 0, 0)
  }, [draw])

  // Idle animation loop (pulsing glow, particle dots)
  useEffect(() => {
    if (busyRef.current) return
    let running = true
    const idle = () => {
      if (!running || busyRef.current) return
      timeRef.current += 0.016
      draw(rotRef.current, timeRef.current, 0)
      frameRef.current = requestAnimationFrame(idle)
    }
    frameRef.current = requestAnimationFrame(idle)
    return () => { running = false; cancelAnimationFrame(frameRef.current) }
  }, [draw, spinning])

  // Reset busyRef when a new spin starts (spinning=true but no targetIndex yet)
  useEffect(() => {
    if (spinning && targetIndex === undefined) {
      // New spin starting - reset busy flag so animation can start when targetIndex arrives
      busyRef.current = false
      cancelAnimationFrame(frameRef.current)
    }
  }, [spinning, targetIndex])

  // COMPLETELY REWRITTEN SPIN ANIMATION - Simple and correct
  useEffect(() => {
    if (!spinning || targetIndex === undefined || busyRef.current) return
    busyRef.current = true
    cancelAnimationFrame(frameRef.current)

    // ══════════════════════════════════════════════════════════════════════════
    // SIMPLE APPROACH: Calculate exact final rotation, then animate to it
    // ══════════════════════════════════════════════════════════════════════════
    
    // The wheel segments are drawn with segment 0 at the TOP (starting at -π/2).
    // When rotation = 0, segment 0 is at the top.
    // To make segment N appear at the top (under the pointer), we need to rotate
    // the wheel CLOCKWISE by N segments worth of angle.
    // 
    // Rotation direction: POSITIVE rotation = CLOCKWISE in canvas (y-axis down)
    // So to move segment N to the top, we rotate by: N * ARC
    // Plus half a segment to center it: (N + 0.5) * ARC
    // But we rotate CLOCKWISE, so it's actually: (N + 0.5) * ARC going forward
    //
    // WAIT - let me think about this more carefully:
    // - At rot=0, segment 0 CENTER is at angle (-π/2 + ARC/2) from wheel center
    // - The pointer is at angle -π/2 (top)
    // - To align segment 0 CENTER with pointer: we need rot = -ARC/2
    // - To align segment 1 CENTER with pointer: we need rot = -ARC/2 - ARC = -1.5*ARC
    // - To align segment N CENTER with pointer: we need rot = -(N + 0.5) * ARC
    //
    // SIMPLIFICATION: Just calculate the exact target rotation directly.
    
    const targetRotation = -(targetIndex + 0.5) * ARC
    
    // Current rotation (could be any value from previous spins)
    const currentRot = rotRef.current
    
    // We want to end at a rotation that is equivalent to targetRotation (mod 2π)
    // but is GREATER than currentRot (so wheel spins forward)
    // and includes several full rotations for visual effect.
    
    // First, normalize targetRotation to [0, 2π)
    const TWO_PI = Math.PI * 2
    const normalizedTarget = ((targetRotation % TWO_PI) + TWO_PI) % TWO_PI
    
    // Find the smallest rotation >= currentRot that lands on normalizedTarget
    // This is: currentRot + (normalizedTarget - (currentRot % TWO_PI) + TWO_PI) % TWO_PI
    const currentNormalized = ((currentRot % TWO_PI) + TWO_PI) % TWO_PI
    let deltaToTarget = normalizedTarget - currentNormalized
    if (deltaToTarget <= 0) deltaToTarget += TWO_PI  // Ensure forward rotation
    
    // Add 8-11 full rotations for dramatic effect
    const extraSpins = (8 + Math.floor(Math.random() * 4)) * TWO_PI
    
    // Final target: current position + extra spins + delta to land on target
    const finalRotation = currentRot + extraSpins + deltaToTarget
    
    console.log('[PrizeWheel] NEW Animation starting:', {
      targetIndex,
      targetSegment: SEGMENTS[targetIndex]?.label,
      targetRotation: targetRotation.toFixed(4),
      normalizedTarget: normalizedTarget.toFixed(4),
      currentRot: currentRot.toFixed(4),
      deltaToTarget: deltaToTarget.toFixed(4),
      extraSpins: (extraSpins / TWO_PI).toFixed(0) + ' full rotations',
      finalRotation: finalRotation.toFixed(4),
    })

    const startRot = currentRot
    const endRot = finalRotation
    const duration = 6000
    const startTime = performance.now()

    const animate = (now: number) => {
      const elapsed = now - startTime
      const progress = Math.min(elapsed / duration, 1)
      // Ease-out quartic for smooth deceleration
      const eased = 1 - Math.pow(1 - progress, 4)
      
      rotRef.current = startRot + (endRot - startRot) * eased
      speedRef.current = progress < 0.1 ? progress * 10 : Math.max(0, 1 - eased)
      timeRef.current += 0.016
      draw(rotRef.current, timeRef.current, speedRef.current)
      
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate)
      } else {
        // Ensure we land EXACTLY on the target rotation
        rotRef.current = endRot
        draw(rotRef.current, timeRef.current, 0)
        
        // Verify the landing
        const landedNormalized = ((rotRef.current % TWO_PI) + TWO_PI) % TWO_PI
        const expectedNormalized = normalizedTarget
        const error = Math.abs(landedNormalized - expectedNormalized)
        
        // Calculate which segment we actually landed on
        // At rotation R, the wheel-local angle at the pointer (-π/2) is: -π/2 - R
        const wheelAngleAtPointer = ((-Math.PI / 2 - rotRef.current) % TWO_PI + TWO_PI) % TWO_PI
        const landedSegment = Math.floor((wheelAngleAtPointer + Math.PI / 2) / ARC) % NUM
        
        console.log('[PrizeWheel] Animation complete:', {
          targetIndex,
          expectedSegment: SEGMENTS[targetIndex]?.label,
          landedSegment,
          landedSegmentLabel: SEGMENTS[landedSegment]?.label,
          finalRotation: rotRef.current.toFixed(4),
          landedNormalized: landedNormalized.toFixed(4),
          expectedNormalized: expectedNormalized.toFixed(4),
          error: error.toFixed(6),
          MATCH: landedSegment === targetIndex ? '✓ CORRECT' : '✗ WRONG!',
        })
        
        busyRef.current = false
        speedRef.current = 0
        onSpinCompleteRef.current?.()
      }
    }
    
    frameRef.current = requestAnimationFrame(animate)
    return () => {
      cancelAnimationFrame(frameRef.current)
      busyRef.current = false
    }
  }, [spinning, targetIndex, draw])

  // Retina resize
  useEffect(() => {
    const cvs = canvasRef.current
    const container = containerRef.current
    if (!cvs || !container) return
    const fit = () => {
      const w = container.getBoundingClientRect().width
      const dpr = window.devicePixelRatio || 1
      const vh = window.innerHeight
      const maxSz = Math.min(vh * 0.48, 540)
      const sz = Math.max(Math.min(w, maxSz), 260)
      cvs.width = sz * dpr; cvs.height = sz * dpr
      cvs.style.width = `${sz}px`; cvs.style.height = `${sz}px`
      draw(rotRef.current, timeRef.current, speedRef.current)
    }
    const ro = new ResizeObserver(fit)
    ro.observe(container)
    return () => ro.disconnect()
  }, [draw])

  return (
    <div
      ref={containerRef}
      className="relative w-full flex items-center justify-center"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        perspective: '800px',
      }}
    >
      <div className="relative inline-block" style={{
        transform: hovered && !spinning ? 'rotateX(3deg) rotateY(-2deg)' : 'rotateX(0deg)',
        transition: 'transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)',
        transformStyle: 'preserve-3d',
      }}>
        <canvas ref={canvasRef} className="block" />
        <button
          onClick={onSpin}
          disabled={disabled || spinning}
          aria-label={spinning ? 'Wheel is spinning' : 'Spin the wheel'}
          style={{ touchAction: 'manipulation' }}
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
            w-[76px] h-[76px] sm:w-[88px] sm:h-[88px] rounded-full
            font-black text-[13px] sm:text-[15px] tracking-[0.18em] uppercase
            border-[3px] transition-all duration-300 z-10
            ${spinning
              ? 'bg-white/90 border-gray-300 text-gray-400 cursor-wait'
              : disabled
                ? 'bg-white/60 border-gray-200 text-gray-300 cursor-not-allowed opacity-40'
                : `bg-white border-[#FF6B00] text-[#FF6B00]
                   shadow-[0_4px_20px_rgba(255,107,0,0.15)]
                   hover:bg-[#FF6B00] hover:text-white
                   hover:shadow-[0_6px_30px_rgba(255,107,0,0.35)]
                   active:scale-90`
            }`}
        >
          {spinning ? (
            <span className="inline-block animate-spin-slow">
              <svg className="w-6 h-6 mx-auto" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
            </span>
          ) : 'SPIN'}
        </button>
      </div>
    </div>
  )
}
